import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Contributions from "./Contributions";
import DevContact from "./DevContact";
import { PROJECTS_DATA, EDUCATION } from "../../constants/projectsData";
import { ABOUT } from "../../constants/about";
import { CASE_STUDIES } from "../../constants/caseStudies";
import { AI_WORK, AI_PRINCIPLES } from "../../constants/aiWork";
import Diagram from "./Diagram";
import useMotion from "./useMotion";
import Background from "./Background";
import { USER_INFO, SITE_CONFIG } from "../../constants/userInfo";
import "./dev.css";
import "./motion.css";

const STATS = [
  { value: "15%", label: "more first-trip conversions", sub: "Uber" },
  { value: "22%", label: "fewer support contacts", sub: "Uber" },
  { value: "25%", label: "more reviews per customer", sub: "Walmart" },
  { value: "18%", label: "more engagement with relevant reviews", sub: "Walmart" },
  { value: "30%", label: "faster page loads", sub: "Beautycounter" },
  { value: "60%", label: "faster API responses", sub: "Marriott" },
];

const HEAT_THEME = {
  dark: {
    level0: "#1b1e25",
    level1: "#0e4429",
    level2: "#006d32",
    level3: "#26a641",
    level4: "#3ddc97",
  },
  light: {
    level0: "#ebedf0",
    level1: "#9be9a8",
    level2: "#40c463",
    level3: "#30a14e",
    level4: "#216e39",
  },
};

const START_YEAR = 2014;

const hash = (n) =>
  (Math.imul(n + 7, 2654435761) >>> 0).toString(16).slice(0, 7).padStart(7, "0");

function useTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("theme") || "dark";
    } catch (e) {
      return "dark";
    }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("theme", theme);
    } catch (e) {
      /* storage unavailable */
    }
  }, [theme]);
  const toggle = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    []
  );
  return [theme, toggle];
}

function CommandPalette({ open, onClose, items }) {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  const run = (item) => {
    onClose();
    if (item) item.run();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(results[index]);
    }
  };

  let lastGroup = null;
  return (
    <div className="pal-overlay" onMouseDown={onClose}>
      <div
        className="pal"
        role="dialog"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="pal-input"
          placeholder="Search sections, projects, actions..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIndex(0);
          }}
          onKeyDown={onKeyDown}
        />
        <div className="pal-list">
          {results.length === 0 && <div className="pal-empty">No results</div>}
          {results.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.label}>
                {header && <div className="pal-group">{header}</div>}
                <button
                  className={"pal-item" + (i === index ? " active" : "")}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => run(item)}
                >
                  <span>{item.label}</span>
                  {item.hint && <span className="kbd">{item.hint}</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Portfolio() {
  const [theme, toggleTheme] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useMotion();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const mail = `mailto:${USER_INFO.email}`;

  // Scroll to the footer contact form, then put the cursor in its first field.
  const goToContact = () => {
    const el = document.getElementById("contact");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      const first = el.querySelector(".cf input");
      if (first) first.focus({ preventScroll: true });
    }, 700);
  };

  const paletteItems = useMemo(
    () => [
      { group: "Sections", label: "Impact", run: scrollTo("impact") },
      { group: "Sections", label: "AI in production", run: scrollTo("ai") },
      { group: "Sections", label: "Career", run: scrollTo("career") },
      { group: "Sections", label: "Case studies", run: scrollTo("work") },
      { group: "Sections", label: "Beyond the code", run: scrollTo("about") },
      { group: "Sections", label: "Contact form", run: scrollTo("contact") },
      ...CASE_STUDIES.map((c) => ({
        group: "Case studies",
        label: `${c.company}: ${c.title}`,
        run: scrollTo(`case-${c.id}`),
      })),
      {
        group: "Actions",
        label: "Email me",
        hint: "E",
        run: () => {
          window.location.href = mail;
        },
      },
      {
        group: "Actions",
        label: "Open GitHub",
        hint: "G",
        run: () => window.open(USER_INFO.githubUrl, "_blank", "noopener,noreferrer"),
      },
      { group: "Actions", label: "Toggle theme", hint: "T", run: toggleTheme },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mail, toggleTheme]
  );

  const yearsBuilding = new Date().getFullYear() - START_YEAR;

  return (
    <div className="dev">
      <Background theme={theme} />
      <div className="vignette" aria-hidden="true" />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={paletteItems}
      />

      <header className="dev-bar">
        <span className="mono dev-logo">
          <img
            className="dev-logo-icon"
            src={`${process.env.PUBLIC_URL}/favicon.svg`}
            alt=""
            width="22"
            height="22"
          />
          <span>
            elicampos.tech<span className="crumb" aria-hidden="true" />
          </span>
        </span>
        <div className="dev-bar-right">
          <button className="dev-ghost" onClick={() => setPaletteOpen(true)}>
            <span className="kbd mono">Ctrl K</span>
            <span className="dev-hide-sm">Jump to anything</span>
          </button>
          <button
            className="dev-ghost"
            onClick={toggleTheme}
            aria-label="Toggle light and dark theme"
          >
            {theme === "dark" ? "Light" : "Dark"}
          </button>
        </div>
      </header>

      <main className="dev-main">
        <section className="hero">
          <div className="pills">
            {SITE_CONFIG.openToWork && (
              <span className="pill">
                <i className="dot" />
                Open to work
              </span>
            )}
            <span className="pill mono">main · {yearsBuilding} years</span>
          </div>
          <h1>
            <span className="hero-name" data-scramble>
              {USER_INFO.name}
            </span>
            <br />
            <span className="muted">I ship AI and software that moves the business.</span>
          </h1>
          <p className="lead">
            Senior full-stack engineer and Gen AI, with {yearsBuilding} years
            tying engineering to results: higher conversion, fewer support
            contacts, faster releases. Lately that means shipping LLM workflows
            and AI agents that are grounded in trusted data and safe to run in
            production.
          </p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={goToContact}>
              Contact me
            </button>
            <button className="btn-ghost" onClick={scrollTo("impact")}>
              See the impact &rarr;
            </button>
            <a
              className="btn-ghost"
              href={USER_INFO.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub ↗
            </a>
          </div>
        </section>

        {SITE_CONFIG.showContributions && (
          <section className="block">
            <div className="drow">
              <h2 className="eyebrow">CONTRIBUTIONS</h2>
              <a
                className="small"
                href={USER_INFO.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View on GitHub ↗
              </a>
            </div>
            <div className="dcard heat">
              <Contributions
                username={USER_INFO.github}
                colorScheme={theme}
                theme={HEAT_THEME[theme]}
              />
            </div>
          </section>
        )}

        <section id="impact" className="block">
          <h2 className="eyebrow">BUSINESS IMPACT</h2>
          <p className="muted section-intro">
            Results I helped deliver, measured in the business outcomes teams
            care about.
          </p>
          <div className="stats">
            {STATS.map((s) => (
              <div className="dcard stat" key={s.label}>
                <div className="stat-value">{s.value}</div>
                <div>{s.label}</div>
                <div className="muted small">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        <section id="ai" className="block">
          <h2 className="eyebrow">AI IN PRODUCTION</h2>
          <p className="muted section-intro">
            AI that shipped to real users, and what it did for the business.
          </p>
          <div className="ai-principles">
            {AI_PRINCIPLES.map((p) => (
              <span className="pill" key={p}>
                {p}
              </span>
            ))}
          </div>
          <div className="ai-grid">
            {AI_WORK.map((a) => (
              <article className="dcard ai-card" key={a.company}>
                <div className="drow">
                  <b>{a.company}</b>
                  <span className="badge-ai mono">AI</span>
                </div>
                <div className="ai-title">{a.title}</div>
                <p className="muted small">{a.what}</p>
                <div className="impact">{a.outcome}</div>
                <div className="muted small ai-how">{a.how}</div>
              </article>
            ))}
          </div>
        </section>

        

        <section id="career" className="block">
          <h2 className="eyebrow">CAREER · GIT LOG</h2>
          <ol className="log">
            {PROJECTS_DATA.map((p) => {
              return (
                <li key={p.id} className="log-item">
                  <i className="log-dot" />
                  <div className="dcard">
                    <div className="drow">
                      <b>{p.company}</b>
                      <span className="mono muted small">{hash(p.id)}</span>
                    </div>
                    <div className="muted small">
                      {p.role} · {p.period}
                    </div>
                    <div className="impact">{p.impact}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section id="work" className="block">
          <h2 className="eyebrow">CASE STUDIES</h2>
          {CASE_STUDIES.map((c, i) => (
            <article className="case" id={`case-${c.id}`} key={c.id}>
              <div className="case-meta mono">
                {String(i + 1).padStart(2, "0")} · {c.company} · {c.role} · {c.period}
              </div>
              <h3 className="case-title">{c.title}</h3>
              <p className="case-problem">{c.problem}</p>
              <h4 className="case-sub">Outcome</h4>
              <div className="case-results">
                {c.results.map((r) => (
                  <div className="dcard stat" key={r.label}>
                    {r.value ? (
                      <>
                        <div className="stat-value">{r.value}</div>
                        <div className="small">{r.label}</div>
                      </>
                    ) : (
                      <div className="outcome-text">{r.label}</div>
                    )}
                  </div>
                ))}
              </div>
              <h4 className="case-sub">Architecture</h4>
              <Diagram diagram={c.diagram} title={c.title} />
              <h4 className="case-sub">Key decisions</h4>
              <div className="decisions">
                {c.decisions.map((d) => (
                  <div className="decision" key={d.title}>
                    <b>
                      {d.title}
                      {d.ai && <span className="badge-ai mono">AI</span>}
                    </b>
                    <p className="muted">{d.text}</p>
                  </div>
                ))}
              </div>
              <div className="case-own">
                <span className="case-own-label mono">my role</span>
                <p>{c.ownership}</p>
              </div>
            </article>
          ))}
        </section>

        <section id="about" className="block">
          <h2 className="eyebrow">BEYOND THE CODE</h2>
          <p className="about-intro">{ABOUT.intro}</p>
          <h3 className="case-sub">How I work</h3>
          <div className="principles">
            {ABOUT.principles.map((p, i) => (
              <div className="principle" key={p.title}>
                <span className="principle-num mono">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <b>{p.title}</b>
                  <p className="muted">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="about-grid">
            <div className="dcard about-card">
              <div className="foot-head">What keeps me going</div>
              <div className="about-pills">
                {ABOUT.drives.map((d) => (
                  <span className="pill" key={d}>
                    {d}
                  </span>
                ))}
              </div>
            </div>
            {ABOUT.hobbies.length > 0 && (
              <div className="dcard about-card">
                <div className="foot-head">Off the clock</div>
                <div className="about-pills">
                  {ABOUT.hobbies.map((h) => (
                    <span className="pill" key={h}>
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <blockquote className="about-quote">“{ABOUT.quote}”</blockquote>
        </section>

        <section id="education" className="block">
          <h2 className="eyebrow">EDUCATION</h2>
          <div className="dcard">
            <div className="drow">
              <b>{EDUCATION.school}</b>
              <span className="mono muted small">{EDUCATION.year}</span>
            </div>
            <div className="muted small">
              {EDUCATION.degree} · {EDUCATION.place}
            </div>
          </div>
        </section>
      </main>

      <footer className="foot">
        <div className="foot-inner">
          <div className="foot-top" id="contact">
            <section className="foot-cta">
              <h2 className="eyebrow">WHAT'S NEXT</h2>
              <p className="foot-title">
                Have a product that needs to move a metric?
              </p>
              <p className="muted foot-text">
                I'm open to senior full-stack and Gen AI roles. Send a message
                here, or email me directly.
              </p>
              <div className="hero-cta">
                <a className="btn-primary" href={mail}>
                  Email me
                </a>
                <a
                  className="btn-ghost"
                  href={USER_INFO.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub ↗
                </a>
              </div>
            </section>
            <DevContact />
          </div>

          <nav className="foot-grid" aria-label="Footer">
            <div className="foot-col">
              <div className="foot-head">Navigate</div>
              {[
                ["Impact", "impact"],
                ["AI in production", "ai"],
                ["Career", "career"],
                ["Case studies", "work"],
                ["Beyond the code", "about"],
              ].map(([label, id]) => (
                <button key={id} className="foot-link" onClick={scrollTo(id)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="foot-col">
              <div className="foot-head">Connect</div>
              <a className="foot-link" href={mail}>
                {USER_INFO.email}
              </a>
              <a
                className="foot-link"
                href={USER_INFO.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                github.com/{USER_INFO.github}
              </a>
              <a
                className="foot-link"
                href={USER_INFO.codepenUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                codepen.io/elicampos-821
              </a>
            </div>
            <div className="foot-col">
              <div className="foot-head">Shortcuts</div>
              <button className="foot-link" onClick={() => setPaletteOpen(true)}>
                <span className="kbd mono">Ctrl K</span> Jump to anything
              </button>
              <button className="foot-link" onClick={toggleTheme}>
                Switch to {theme === "dark" ? "light" : "dark"} theme
              </button>
            </div>
          </nav>
        </div>

        <div className="statusbar mono">
          <div className="status-left">
            {SITE_CONFIG.openToWork && (
              <span className="status-item">
                <i className="dot" /> open to work
              </span>
            )}
            <span className="status-item">⎇ main</span>
            <span className="status-item status-hide-sm">
              {yearsBuilding} years · {CASE_STUDIES.length} case studies
            </span>
          </div>
          <div className="status-right">
            <a
              className="status-item status-link"
              href={USER_INFO.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              github ↗
            </a>
            <a
              className="status-item status-link"
              href={USER_INFO.codepenUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              codepen ↗
            </a>
            <span className="status-item status-hide-sm">
              © {new Date().getFullYear()} {USER_INFO.name}
            </span>
            <button
              className="status-item status-top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              back to top ↑
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Portfolio;
